import { loadModules } from "esri-loader";
import { arcgisToGeoJSON } from "@esri/arcgis-to-geojson-utils";
import Signal from "signals";
import moment from "moment";

import { writable, get } from "svelte/store";
import LoadState from "../Enums/LoadState";
import GeoHelper from "../Helpers/GeoHelper";
import MapStore from "../Map/Store";
import PopupStore from "../Map/Popup/PopupStore";
import VisibilityState from "../Map/Enums/VisibilityState";
import StringHelper from "../Helpers/StringHelper";
import MapLayerManager from "./MapLayerManager";

const DEFAULT_PAINT = {
	"circle-radius": 5,
	"circle-color": "rgba(0, 0, 0, 0.35)",
	"circle-stroke-color": "rgba(0, 0, 0, 1.0)",
	"circle-stroke-width": 1.5
};
class SimpleESRIPointLayer {
	constructor(name) {
		this.config = {
			verbose: false
			// token: "",
			// serverRoot: ""
		};
		this.events = {
			onLoaded: new Signal()
		};
		this.name = name;
		this.visible = writable(false);

		this.meta = {
			loadState: writable(LoadState.INIT),
			lastUpdated: writable(Date.now()),
			mapLayerGroup: "",
			layers: [],
			layerData: {},
			_esriLayerData: [], // Items from this are passed into esri.layers.FeatureLayer constructors.
			_esriLayerQueryAttributes: [],
			_response: null
		};

		// Initialise the layer when its turned on.
		this.visible.subscribe((visible) => {
			if (visible) {
				this.init();
			}
			if (get(this.meta.loadState) === LoadState.LOADED) {
				this.updateVisibility();
			}
		});
	}

	get responses() {
		return this.meta._response;
	}

	setMapLayerGroup(mapLayerGroup) {
		this.meta.mapLayerGroup = mapLayerGroup;
	}

	addDataLayer(obj, queryAttributes = null) {
		this.meta._esriLayerData.push(obj);
		this.meta._esriLayerQueryAttributes.push(queryAttributes);
	}

	addDisplayLayer(key, type = "circle", paint = DEFAULT_PAINT, esriFeatureCollection = null) {
		this.meta.layerData[key] = this._initLayerData();
		this.meta.layerData[key].type = type;
		this.meta.layerData[key].paint = paint;
		if (esriFeatureCollection !== null) {
			this._addDisplayLayerFeatures(key, esriFeatureCollection);
		}
	}

	getDisplayLayer(key) {
		return this.meta.layerData[key];
	}

	_addDisplayLayerFeatures(key, esriFeatureCollection) {
		const featureCollection = this._esriFeatureCollectionToGeoJSON(esriFeatureCollection);
		// console.log('featureCollection', featureCollection);
		const displayLayer = this.meta.layerData[key];
		const dataLayerKey = key + "_" + displayLayer.layers.length;
		displayLayer.layers.push(dataLayerKey);
		this._addMapboxLayer(key, dataLayerKey, displayLayer.type, displayLayer.paint, featureCollection);
		this.updateVisibility();
	}

	_esriFeatureCollectionToGeoJSON(esriFeatureCollection) {
		const layer = esriFeatureCollection;
		const featureCollection = GeoHelper.emptyGeoJson();
		for (let i = 0; i < layer.features.length; i++) {
			const feature = arcgisToGeoJSON(layer.features[i]);
			if (feature === null) {
				console.warn("feature index was null. skipping.", i);
				continue;
			}
			if (feature.geometry === null) {
				console.warn("feature (geometry) index was null. skipping.", i);
				continue;
			}
			if (!layer.spatialReference.isWGS84) {
				GeoHelper.forEachFeatureCoordinateRef(feature, (coords) => {
					coords = GeoHelper.metersToDegrees(coords[0], coords[1]);
					return coords;
				});
			}
			featureCollection.features.push(feature);
		}
		return featureCollection;
	}

	setDisplayLayerPopups(key, popupData, options = {}) {
		this.meta.layerData[key].popupData = popupData;

		const map = MapStore.helpers.getMap();
		const onEnterPoint = (e) => {
			if (e.features.length > 0) {
				if (this.config.verbose) {
					console.log(e.features[0]);
				}
				// const props = {
				// 	Name: e.features[0].properties.Title
				// };
				// PopupStore.actions.custom(props, e.features[0].geometry.coordinates, true);
				let html = "";
				for (let i = 0; i < popupData.length; i++) {
					const pd = popupData[i];
					let val = e.features[0].properties[pd.key];
					if (val === null || val === "null" || val === "" || val === " ") {
						continue;
					}
					if (pd.type === "title") {
						html += "<div><center>";
						if (pd.linkKey) {
							html += "<a href='" + e.features[0].properties[pd.linkKey] + "'>";
						}
						html += "<b>" + val + "</b>";
						if (pd.linkKey) {
							html += "</a>";
						}
						html += "</center></div>";
					}
					else if (pd.type === "keypair") {
						html += "<div><b>" + pd.name + "</b>: " + val + "</div>";
					}
					else if (pd.type === "description") {
						if (pd.maxLength > 0 && val.length > pd.maxLength) {
							val = val.substr(0, pd.maxLength).trim() + "...";
						}
						html += "<div>" + val + "</div>";
					}
					else if (pd.type === "date") {
						if (pd.name) {
							html += "<div><b>" + pd.name + "</b>: <i>" + moment.utc(val).format("MMMM Do YYYY, hh:mm:ssa [(UTC)]") + "</i></div>";
						}
						else {
							html += "<div><center><small><i>" + moment.utc(val).format("MMMM Do YYYY, hh:mm:ssa [(UTC)]") + "</i></small></center></div>";
						}
					}
				}
				PopupStore.actions.customHtml(html, 400, e.features[0].geometry.coordinates, true);

				// const popup = PopupStore.helpers.getInstance();
				// console.log("popup", popup);
			}
		};
		const onLeavePoint = () => {
			// popup.options.closeButton = true;
			PopupStore.actions.custom({}, null, false);
		};

		const onClickPoint = (e) => {
			if (e.features.length > 0) {
				if (options.linkKey) {
					const url = e.features[0].properties[options.linkKey];
					if (url !== null && url !== "null" && url !== "" && url !== "about:blank") {
						window.open(url);
					}
					else {
						// PopupElementType.DESCRIPTION;
						// IncidentDescription;
						let descriptionValue = null;
						for (let i = 0; i < popupData.length; i++) {
							if (popupData[i].type === "description") {
								descriptionValue = e.features[0].properties[popupData[i].key];
								break;
							}
						}
						if (descriptionValue !== null) {
							console.log("descriptionValue", descriptionValue);
							const url = StringHelper.findUrl(descriptionValue);
							console.log("url", url);
							if (url !== null) {
								window.open(url);
							}
						}
					}
				}
			}
		};
		map.on("click", this.meta.layerData[key].layers[0], onClickPoint);

		map.on("mouseenter", this.meta.layerData[key].layers[0], onEnterPoint);
		map.on("mousemove", this.meta.layerData[key].layers[0], onEnterPoint);
		map.on("mouseleave", this.meta.layerData[key].layers[0], onLeavePoint);
	}

	_initLayerData() {
		return {
			layers: [],
			type: null,
			paint: null,
			featureCollection: writable(null),
			visible: writable(true)
		};
	}

	_addMapboxLayer(key, name, type, paint, featureCollection) {
		const map = MapStore.helpers.getMap();
		map.addSource(name, {
			type: "geojson",
			data: featureCollection
		});
		map.addLayer(
			{
				id: name,
				type: type,
				source: name,
				paint: paint
			}
		);
		this.meta.layers.push(name);
		this.meta.layerData[key].featureCollection.set(featureCollection);
	}

	init() {
		// Already initted
		if (get(this.meta.loadState) !== LoadState.INIT) {
			return;
		}

		this.meta.loadState.set(LoadState.LOADING);
		console.log("Loading " + this.name + "...");

		// const token = this.config.token;
		// const server = this.config.serverRoot;

		loadModules(["esri/Map", "esri/layers/FeatureLayer", "esri/identity/IdentityManager", "esri/tasks/support/Query"])
			.then((classes) => {
				const FeatureLayer = classes[1];
				// const IdentityManager = classes[2];
				const Query = classes[3];

				// if (token.length > 0) {
				// 	IdentityManager.registerToken({
				// 		token: token,
				// 		server: server
				// 	});
				// }

				const esriDataLayers = this.meta._esriLayerData.map((item) => {
					return new FeatureLayer(item);
				});
				const esriPromises = esriDataLayers.map((featureLayer, index) => {
					if (this.meta._esriLayerQueryAttributes[index] !== null) {
						const query = new Query();
						const keys = Object.keys(this.meta._esriLayerQueryAttributes[index]);
						for (const keyIndex in keys) {
							const key = keys[keyIndex];
							query[key] = this.meta._esriLayerQueryAttributes[index][key];
						}
						return featureLayer.queryFeatures(query);
					}
					return featureLayer.queryFeatures();
				});

				Promise.all(esriPromises)
					.then((responses) => {
						console.log("Loaded " + this.name + "...", responses);
						this.meta._response = responses;
						this.meta.loadState.set(LoadState.LOADED);
						this.meta.lastUpdated.set(Date.now());

						MapLayerManager.addLayerGroup(this.meta.mapLayerGroup, this.meta.layers, this.meta.layers);

						this.events.onLoaded.dispatch(responses);
					});
			});
	}

	updateVisibility() {
		const map = MapStore.helpers.getMap();
		if (map) {
			const visible = get(this.visible);
			const dataLayers = Object.keys(this.meta.layerData);
			for (let i = 0; i < dataLayers.length; i++) {
				const layer = this.meta.layerData[dataLayers[i]];
				for (let j = 0; j < layer.layers.length; j++) {
					const layerVisible = get(layer.visible);
					map.setLayoutProperty(layer.layers[j], "visibility", (visible && layerVisible) ? VisibilityState.VISIBLE : VisibilityState.NONE);
				}
			}
		}
	}
}
export default SimpleESRIPointLayer;
